import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

const PAGE_SIZE = 20;
const STATUSES = ["ALL", "PENDING", "APPROVED", "REJECTED", "ARCHIVED"];

export default function ModerationPosts() {
  const [q, setQ] = useState("");
  const [type, setType] = useState("ALL"); // ALL | PRAYER | PRAISE
  const [status, setStatus] = useState("ALL"); // ALL | PENDING | APPROVED | REJECTED | ARCHIVED
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [viewOpen, setViewOpen] = useState(false);
  const [viewItem, setViewItem] = useState(null);

  useEffect(() => {
    document.documentElement.setAttribute(
      "data-theme",
      localStorage.getItem("theme") || "light"
    );
  }, []);

  useEffect(() => {
    let cancel = false;
    const controller = new AbortController();
    async function fetchData() {
      setLoading(true);
      setErr("");
      try {
        const params = new URLSearchParams({
          page: String(page),
          pageSize: String(PAGE_SIZE),
          q: q.trim(),
        });
        if (type !== "ALL") params.set("type", type);
        if (status !== "ALL") params.set("status", status);
        // For admin, backend allows omitting type. If type is ALL, do not include it.
        const res = await axios.get(`/api/community/admin/posts?${params.toString()}`, {
          withCredentials: true,
          signal: controller.signal,
        });
        const data = res?.data || {};
        if (!cancel) {
          setItems(data.items || []);
          setTotal(data.total || 0);
        }
      } catch (e) {
        if (!cancel) setErr("Failed to load posts");
      } finally {
        if (!cancel) setLoading(false);
      }
    }
    fetchData();
    return () => {
      cancel = true;
      controller.abort();
    };
  }, [q, type, status, page]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const hasActiveFilters = useMemo(() => {
    return q.trim() || type !== "ALL" || status !== "ALL";
  }, [q, type, status]);

  const clearFilters = () => {
    setQ("");
    setType("ALL");
    setStatus("ALL");
    setPage(1);
  };

  async function updateStatus(id, newStatus) {
    try {
      await axios.patch(
        `/api/community/posts/${id}/status`,
        { status: newStatus },
        { withCredentials: true }
      );
      setItems((arr) => arr.map((it) => (it._id === id ? { ...it, status: newStatus } : it)));
      const msg = newStatus === "APPROVED" ? "Approved post"
        : newStatus === "REJECTED" ? "Rejected post"
        : newStatus === "ARCHIVED" ? "Archived post"
        : "Updated post";
      toast.success(msg);
    } catch {}
  }

  async function openView(id) {
    try {
      const res = await axios.get(`/api/community/admin/posts/${id}`, { withCredentials: true });
      setViewItem(res?.data || null);
      setViewOpen(true);
    } catch {
      setViewItem(null);
      setViewOpen(true);
    }
  }

  return (
    <div className="p-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Community ▸ Posts Moderation</h2>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <input
          value={q}
          onChange={(e) => { setPage(1); setQ(e.target.value); }}
          placeholder="Search title/content…"
          className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:outline-none"
        />
        <select 
          value={type} 
          onChange={(e) => { setPage(1); setType(e.target.value); }} 
          className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:outline-none"
        >
          <option value="ALL">All Types</option>
          <option value="PRAYER">Prayer</option>
          <option value="PRAISE">Praise</option>
        </select>
        <select 
          value={status} 
          onChange={(e) => { setPage(1); setStatus(e.target.value); }} 
          className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:outline-none"
        >
          <option value="ALL">All Status</option>
          <option value="PENDING">Pending</option>
          <option value="APPROVED">Approved</option>
          <option value="REJECTED">Rejected</option>
          <option value="ARCHIVED">Archived</option>
        </select>
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
          >
            Clear Filters
          </button>
        )}
      </div>

      <div className="mt-4 rounded-xl border bg-white overflow-hidden">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2">Title</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Created</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td className="px-3 py-6 text-center" colSpan={5}>Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td className="px-3 py-6 text-center" colSpan={5}>No items</td></tr>
            ) : (
              items.map((it) => (
                <tr key={it._id} className="border-t">
                  <td className="px-3 py-2 text-xs">{it.type}</td>
                  <td className="px-3 py-2 max-w-[360px] truncate">{it.title}</td>
                  <td className="px-3 py-2 text-xs">{it.status}</td>
                  <td className="px-3 py-2 text-xs">{new Date(it.createdAt).toLocaleString()}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2 justify-end">
                      <button onClick={() => openView(it._id)} className="rounded border px-2 py-1 text-xs hover:bg-gray-50" title="View">
                        👁️
                      </button>
                      <select
                        value={it.status}
                        onChange={(e) => updateStatus(it._id, e.target.value)}
                        className="rounded border border-gray-200 px-2 py-1 text-xs bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                      >
                        <option value="PENDING">Pending</option>
                        <option value="APPROVED">Approved</option>
                        <option value="REJECTED">Rejected</option>
                        <option value="ARCHIVED">Archived</option>
                      </select>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-2">
          <button className="rounded border px-3 py-1.5" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</button>
          <span>Page {page} / {totalPages}</span>
          <button className="rounded border px-3 py-1.5" disabled={page === totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next</button>
        </div>
      )}

      {viewOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-3xl max-h-[90vh] rounded-2xl bg-white shadow-xl flex flex-col">
            {/* Header - Fixed */}
            <div className="flex items-center justify-between p-5 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-lg font-semibold">{viewItem ? `${viewItem.type} ▸ ${viewItem.status}` : "Post detail"}</h3>
              <button 
                onClick={() => setViewOpen(false)} 
                className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 transition-colors duration-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-5">
              {!viewItem ? (
                <div className="py-8 text-center text-slate-600">Unable to load</div>
              ) : (
                <div className="space-y-4">
                  <div className="text-sm text-slate-600">
                    {new Date(viewItem.createdAt).toLocaleString()}
                  </div>
                  {viewItem.title && (
                    <h4 className="text-xl font-semibold text-gray-900">{viewItem.title}</h4>
                  )}
                  <div 
                    className="break-words text-gray-800 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: viewItem.content }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


